import { useCookies } from '@fergdigitalcommerce/fergy-react-components';
import loadable from '@loadable/component';
import { useEffect, useRef, useState, type FunctionComponent } from 'react';
import { useInView } from 'react-intersection-observer';
import { configureRoute } from '../../../utils/routes/configure-route';
import { PageLoading } from '../../components/common-components/loading/loading.component';
import { PageContainer } from '../../components/common-components/page/page-container/page-container.component';
import { CornerRibbon } from '../../components/corner-ribbon/corner-ribbon.component';
import { FiveHundred } from '../../components/error-components/500/500.component';
import { FacetDrawer } from '../../components/search-components/facet-drawer/facet-drawer.component';
import { FacetFilterList } from '../../components/search-components/facet-filter-list/facet-filter-list.component';
import { FacetList } from '../../components/search-components/facet-list/facet-list.component';
import { SearchHeader } from '../../components/search-components/search-header/search-header.component';
import { SearchHeaderControls } from '../../components/search-components/search-header-controls/search-header-controls.component';
import { SearchHeaderTitle } from '../../components/search-components/search-header-title/search-header-title.component';
import { SimilarSearches } from '../../components/search-components/similar-searches/similar-searches.component';
import { COOKIE_LAST_SEARCH } from '../../constants/cookies';
import { APPENDED_SITE_NAME, FEATURE_FLAGS } from '../../constants/general';
import { SEARCH_ROUTE } from '../../constants/links';
import { SEARCH_HEADER_ELEMENT } from '../../constants/search';
import { AnalyticsHelper } from '../../helpers/analytics/analytics.helper';
import { TrackedEvent, TrackedEventCase } from '../../helpers/analytics/event-types';
import { buildGTMSearchRedirect } from '../../helpers/analytics/gtm/gtm-utils.helper';
import { buildGTMSearch } from '../../helpers/analytics/gtm/search-event-builders';
import {
	generatePageViewSearchDataLayer,
	getCollectionNameFromResults,
	getTrackingSearchTermFromDropType
} from '../../helpers/search-helper/search-analytics.helper';
import { parseTopFacetsFeature } from '../../helpers/search-helper/search-helper';
import { useTrackEvent, useTrackPageView } from '../../hooks/analytics/analytics.hooks';
import { useTrackImpressions } from '../../hooks/analytics/impressions.hooks';
import { useSiteViewPreference } from '../../hooks/apollo/employee/employee.hooks';
import { useGetSearchSource, useProductViewPreference, useSearchResults } from '../../hooks/apollo/search/search.hooks';
import { DyRecommendationContextType, useDyRecommendationContext } from '../../hooks/dynamic-yield/dynamic-yield-client.hooks';
import { useFeature } from '../../hooks/features/features.hooks';
import { useNavigation } from '../../hooks/navigation/navigation.hooks';
import { useRouterLocation } from '../../hooks/routing/location.hook';
import type { ProductSearchResult, SearchRequestData } from '../../types/search.types';
import { isChromatic } from '../../utils/dom/chromatic.utils';

const LoadableSearchResults = loadable(
	() => import(/* webpackChunkName: "search-results" */ '../../components/search-components/search-results/search-results.component'),
	{
		resolveComponent: ({ SearchResults }) => SearchResults
	}
);

const LoadableSearchFooter = loadable(
	() => import(/* webpackChunkName: "search-footer" */ '../../components/search-components/search-footer/search-footer.component'),
	{
		resolveComponent: ({ SearchFooter }) => SearchFooter
	}
);

const LoadableNoSearchResults = loadable(
	() =>
		import(
			/* webpackChunkName: "no-search-results" */ '../../components/search-components/no-search-results/no-search-results.component'
		),
	{
		resolveComponent: ({ NoSearchResults }) => NoSearchResults
	}
);

const LoadableNoFilteredSearchResults = loadable(
	() =>
		import(
			/* webpackChunkName: "no-filtered-search-results" */ '../../components/search-components/no-filtered-search-results/no-filtered-search-results.component'
		),
	{
		resolveComponent: ({ NoFilteredSearchResults }) => NoFilteredSearchResults
	}
);

const LoadableTopFacetsList = loadable(
	() => import(/* webpackChunkName: "top-facets" */ '../../components/search-components/top-facets/top-facets.component'),
	{
		resolveComponent: ({ TopFacetsList }) => TopFacetsList
	}
);

const LoadableSolrAlertNotification = loadable(
	() =>
		import(
			/* webpackChunkName: "solr-alert-notification" */ '../../components/search-components/solr-alert-notification/solr-alert-notification.component'
		),
	{
		resolveComponent: ({ SolrAlertNotification }) => SolrAlertNotification
	}
);

type SearchHeaderSectionProps = {
	useInlineHeader: boolean | undefined;
	headerTitle: string;
	showAsEmployee: boolean;
	showFacetDrawer: () => void;
	topFacetsSettings: { type: string; images: boolean };
	searchResults: ProductSearchResult;
};

export const SearchHeaderSection: FunctionComponent<SearchHeaderSectionProps> = ({
	useInlineHeader,
	headerTitle,
	showAsEmployee,
	showFacetDrawer,
	topFacetsSettings,
	searchResults
}) => {
	return (
		<>
			{useInlineHeader ? (
				<div className="ml3-l mb3">
					<div id={SEARCH_HEADER_ELEMENT} className="flex flex-wrap flex-nowrap-l justify-between-ns items-start ml2-l ga2">
						<SearchHeaderTitle />
						<SearchHeaderControls
							showNonstock={showAsEmployee}
							onShowDrawer={showFacetDrawer}
							className="w-100 w-auto-l flex-auto flex-shrink-0 justify-end-l"
						/>
					</div>
					{searchResults.selectedFacetGroups.length > 0 && (
						<div className="dn db-l mt2 ml2-l">
							<FacetFilterList />
						</div>
					)}
				</div>
			) : (
				<>
					<SearchHeader title={headerTitle} showNonstock={showAsEmployee} onShowDrawer={showFacetDrawer} />
					{topFacetsSettings.type === 'list' && searchResults.topFacets && (
						<LoadableTopFacetsList items={searchResults.topFacets} showImages={topFacetsSettings.images} className="mt3" />
					)}
					<div className="dn db-l mt2">
						<FacetFilterList />
					</div>
				</>
			)}
		</>
	);
};

export const SearchPage: FunctionComponent = () => {
	const { pathname, search } = useRouterLocation();
	const navigate = useNavigation();
	const trackEvent = useTrackEvent();
	const { employee, showAsEmployee } = useSiteViewPreference();
	const { productViewPreference } = useProductViewPreference();
	useDyRecommendationContext({ type: DyRecommendationContextType.OTHER_PAGE });

	const topFacetsFeature = useFeature<string>(FEATURE_FLAGS.KEYWORD_SEARCH_TOP_FACETS);
	const topFacetsSettings = parseTopFacetsFeature(topFacetsFeature);
	const useInlineHeader = useFeature(FEATURE_FLAGS.KEYWORD_SEARCH_INLINE_HEADER);

	/**
	 * We set this last search cookie to be compliant with other pages that
	 * use this data (including pages on node-store)
	 */
	const { setCookie } = useCookies();
	setCookie(COOKIE_LAST_SEARCH, `${pathname}${search}`);

	const { query, results, previousResults, page, pageSize, sortBy, loading, error, redirectUrl, request, isNonstock, flagged } =
		useSearchResults();

	const productSearchResult = previousResults?.products?.length ? previousResults : null;
	const searchResults = loading ? productSearchResult : results;
	const searchEngine = searchResults?.searchEngine;

	const [isFacetDrawerOpen, setFacetDrawerOpen] = useState<boolean>(false);
	const showFacetDrawer = () => setFacetDrawerOpen(true);
	const hideFacetDrawer = () => setFacetDrawerOpen(false);

	/* track the page view */
	const pageName = 'search:browse';
	const dropType = 'search';
	const searchSource = useGetSearchSource();
	const searchDataLayer = generatePageViewSearchDataLayer(
		pageName,
		dropType,
		results,
		page,
		pageSize,
		sortBy,
		query,
		searchSource,
		productViewPreference,
		isNonstock
	);
	/* if the page is still loading or if we have a search redirect, don't track this page view */
	const blockTracking = loading || Boolean(redirectUrl);
	useTrackPageView({ pageName, collection: getCollectionNameFromResults(results) }, searchDataLayer, blockTracking);
	useTrackImpressions(
		TrackedEvent.SEARCH_RESULTS_VIEW,
		getTrackingSearchTermFromDropType(dropType),
		request,
		results,
		productViewPreference,
		isNonstock
	);

	// Search tracking.
	const previousRequestRef = useRef<SearchRequestData | null>(null);
	useEffect(() => {
		if (!loading && !error && request !== previousRequestRef.current) {
			// If the user changes the search terms, we consider this a new search, as opposed to refining the current
			// search with a filter, a change in sort order, etc.
			const isNewSearch = request.query !== previousRequestRef.current?.query;
			trackEvent(
				buildGTMSearch(
					TrackedEvent.SEARCH,
					TrackedEventCase.KEYWORD_SEARCH_RESULTS,
					request,
					results,
					productViewPreference,
					employee.isAuthenticated,
					isNonstock,
					isNewSearch,
					previousRequestRef.current?.query,
					flagged
				)
			);

			previousRequestRef.current = request;
		}
	}, [request, loading, error, isNonstock, productViewPreference, results, trackEvent, employee, flagged]);

	const isRedirecting = useRef(false);
	useEffect(() => {
		if (redirectUrl && !isRedirecting.current) {
			isRedirecting.current = true;
			void AnalyticsHelper.track(buildGTMSearchRedirect(query)).finally(() => navigate(redirectUrl, true));
		}
	}, [redirectUrl, query, navigate]);

	const { ref: footerRef, inView: footerInView } = useInView({
		triggerOnce: true
	});

	if (error) {
		return <FiveHundred />;
	}

	const metaQueryText = flagged ? 'Search' : query;
	const metaDescription = `Save on ${metaQueryText} from Build.com. Low Prices + Fast & Free Shipping on Most Orders. Find reviews, expert advice, manuals, specs & more.`;
	const canonicalURL = flagged ? '' : `${pathname}${search}`;
	const headerTitle = flagged ? metaQueryText : searchResults?.correctedSearchTerm || query;
	const pageTitle = `${metaQueryText}${APPENDED_SITE_NAME}`;

	return (
		<>
			{showAsEmployee && <LoadableSolrAlertNotification searchEngine={searchEngine ?? ''} />}
			<PageContainer
				canonicalURL={canonicalURL}
				metaDescription={metaDescription}
				pageTitle={pageTitle}
				shouldBlockIndexing={true}
				breadcrumbs={(searchResults && searchResults.breadcrumbs) || []}
				showUpdateLocation={true}>
				<>
					{searchResults?.products ? (
						searchResults.products.length > 0 || searchResults.selectedFacetGroups.length > 0 ? (
							<>
								{!useInlineHeader && (
									<SearchHeaderSection
										useInlineHeader={useInlineHeader}
										headerTitle={headerTitle}
										showAsEmployee={showAsEmployee}
										showFacetDrawer={showFacetDrawer}
										topFacetsSettings={topFacetsSettings}
										searchResults={searchResults}
									/>
								)}
								<div className={`flex ${useInlineHeader ? '' : 'mt3 mt4-l'}`} data-automation="search-results-page">
									<div className="w-25-l dn db-l">
										<FacetList />
									</div>
									<div className="w-100 w-75-l h-100 relative">
										{useInlineHeader && (
											<SearchHeaderSection
												useInlineHeader={useInlineHeader}
												headerTitle={headerTitle}
												showAsEmployee={showAsEmployee}
												showFacetDrawer={showFacetDrawer}
												topFacetsSettings={topFacetsSettings}
												searchResults={searchResults}
											/>
										)}
										<LoadableSearchResults viewType={productViewPreference} dropType={dropType} />
										{searchResults.products.length === 0 ? <LoadableNoFilteredSearchResults /> : null}
										<div ref={footerRef} className="ml3-l mb3">
											{(footerInView || isChromatic()) && (
												<>
													<LoadableSearchFooter />
													{/* Delaying the load of SimilarSearches because it relies on cookies that are set by Ensighten on page load */}
													<SimilarSearches />
												</>
											)}
										</div>
									</div>
								</div>
								{isFacetDrawerOpen && <FacetDrawer onFacetDrawerClose={hideFacetDrawer} />}
								<div id="dy-recs-search-page-1" />
							</>
						) : (
							<LoadableNoSearchResults query={query} isNonstock={isNonstock} hideQuery={flagged} />
						)
					) : (
						<PageLoading />
					)}
					{showAsEmployee && searchEngine ? <CornerRibbon text={`${searchEngine} Search`} /> : null}
				</>
			</PageContainer>
		</>
	);
};

export const RoutedSearchPage = configureRoute({ path: SEARCH_ROUTE, ddRum: { rumViewName: 'Search' } }, SearchPage);
